/**
 * Deal Service
 * Handles all deal-related API calls including CRUD operations,
 * stage management, filtering, and statistics
 */

import { apiClient, buildQueryParams, ApiResponse } from './api.client';
import { unwrapListResponse } from './api.unwrap';
import {
  Deal,
  CreateDealRequest,
  UpdateDealRequest,
  UpdateDealStageRequest,
  ListDealsRequest,
  PaginatedResponse,
  DealStage,
  DealPriority,
} from '@creatoros/shared';

/**
 * Deal statistics interface
 */
export interface DealStatistics {
  totalDeals: number;
  totalValue: number;
  dealsByStage: Record<DealStage, number>;
  dealsByPriority: Record<DealPriority, number>;
  averageDealValue: number;
  winRate: number;
}

/**
 * Deal Service Class
 */
class DealService {
  private readonly basePath = '/api/deals';

  /**
   * Get all deals with optional filtering and pagination
   * @param params - List parameters including filters and pagination
   * @returns Paginated list of deals
   */
  async list(params?: ListDealsRequest): Promise<PaginatedResponse<Deal>> {
    const queryParams = params ? buildQueryParams(params as Record<string, unknown>) : {};

    const response = await apiClient.get(this.basePath, {
      params: queryParams,
    });

    return unwrapListResponse<Deal>(response.data) as unknown as PaginatedResponse<Deal>;
  }

  /**
   * Get a single deal by ID
   * @param id - Deal ID
   * @returns Deal data
   */
  async getById(id: string): Promise<Deal> {
    const response = await apiClient.get<ApiResponse<Deal>>(`${this.basePath}/${id}`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch deal');
  }

  /**
   * Create a new deal
   * @param data - Deal creation data
   * @returns Created deal
   */
  async create(data: CreateDealRequest): Promise<Deal> {
    try {
      const response = await apiClient.post<ApiResponse<Deal>>(this.basePath, data);

      if (response.data.success && response.data.data) {
        return response.data.data;
      }

      // Extract error message from response (shouldn't reach here for errors)
      const errorMessage = response.data.error?.message || 'Failed to create deal';
      throw new Error(errorMessage);
    } catch (error: unknown) {
      // Check if error already has a message (ApiClientError extends Error)
      if (
        error instanceof Error &&
        error.message &&
        error.message !== 'Request failed with status code 400'
      ) {
        throw error;
      }

      // Handle raw axios error with response data
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as {
          response?: { data?: { error?: string | { message?: string }; message?: string } };
        };
        const responseData = axiosError.response?.data;
        if (responseData) {
          // Try error field first (backend format)
          const errData = responseData.error;
          if (errData) {
            const message = typeof errData === 'string' ? errData : errData.message;
            if (message) throw new Error(message);
          }
          // Try message field
          if (responseData.message) {
            throw new Error(responseData.message);
          }
        }
      }

      throw new Error('Failed to create deal');
    }
  }

  /**
   * Update an existing deal
   * @param id - Deal ID
   * @param data - Deal update data
   * @returns Updated deal
   */
  async update(id: string, data: UpdateDealRequest): Promise<Deal> {
    const response = await apiClient.patch<ApiResponse<Deal>>(`${this.basePath}/${id}`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to update deal');
  }

  /**
   * Delete a deal
   * @param id - Deal ID
   */
  async delete(id: string): Promise<void> {
    await apiClient.delete<ApiResponse<void>>(`${this.basePath}/${id}`);
  }

  /**
   * Update deal stage
   * @param id - Deal ID
   * @param data - Stage update data
   * @returns Updated deal
   */
  async updateStage(id: string, data: UpdateDealStageRequest): Promise<Deal> {
    const response = await apiClient.patch<ApiResponse<Deal>>(`${this.basePath}/${id}/stage`, data);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to update deal stage');
  }

  /**
   * Get deals by stage
   * @param stage - Deal stage to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of deals in specified stage
   */
  async getByStage(
    stage: DealStage,
    params?: Omit<ListDealsRequest, 'stage'>
  ): Promise<PaginatedResponse<Deal>> {
    return this.list({
      ...params,
      stage,
    });
  }

  /**
   * Get deals by priority
   * @param priority - Deal priority to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of deals with specified priority
   */
  async getByPriority(
    priority: DealPriority,
    params?: Omit<ListDealsRequest, 'priority'>
  ): Promise<PaginatedResponse<Deal>> {
    return this.list({
      ...params,
      priority: priority,
    });
  }

  /**
   * Get deals by brand
   * @param brandId - Brand ID to filter by
   * @param params - Additional list parameters
   * @returns Paginated list of deals for specified brand
   */
  async getByBrand(
    brandId: string,
    params?: Omit<ListDealsRequest, 'brandId'>
  ): Promise<PaginatedResponse<Deal>> {
    return this.list({
      ...params,
      brandId,
    });
  }

  /**
   * Search deals by name or description
   * @param query - Search query string
   * @param params - Additional list parameters
   * @returns Paginated list of matching deals
   */
  async search(
    query: string,
    params?: Omit<ListDealsRequest, 'search'>
  ): Promise<PaginatedResponse<Deal>> {
    return this.list({
      ...params,
      search: query,
    });
  }

  /**
   * Get deal statistics
   * @param filters - Optional filters for statistics
   * @returns Deal statistics
   */
  async getStats(filters?: {
    startDate?: string;
    endDate?: string;
    brandId?: string;
  }): Promise<DealStatistics> {
    const queryParams = filters ? buildQueryParams(filters as Record<string, unknown>) : {};

    const response = await apiClient.get<ApiResponse<DealStatistics>>(`${this.basePath}/stats`, {
      params: queryParams,
    });

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch deal statistics');
  }

  /**
   * Get stage history for a deal
   * @param id - Deal ID
   * @returns Stage history entries
   */
  async getStageHistory(id: string): Promise<Deal['stageHistory']> {
    const response = await apiClient.get<ApiResponse<Deal['stageHistory']>>(
      `${this.basePath}/${id}/stage-history`
    );

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to fetch stage history');
  }

  /**
   * Get all deliverables for a deal
   * @param id - Deal ID
   * @param params - Pagination parameters
   * @returns List of deliverables for the deal
   */
  async getDeliverables(
    id: string,
    params?: { page?: number; limit?: number }
  ): Promise<PaginatedResponse<unknown>> {
    const queryParams = params ? buildQueryParams(params as Record<string, unknown>) : {};

    const response = await apiClient.get<PaginatedResponse<unknown>>(
      `${this.basePath}/${id}/deliverables`,
      { params: queryParams }
    );

    if (response.data.success && response.data.data) {
      return response.data;
    }

    throw new Error('Failed to fetch deal deliverables');
  }

  /**
   * Get all notes for a deal
   * @param id - Deal ID
   * @param params - Pagination parameters
   * @returns List of notes for the deal
   */
  async getNotes(
    id: string,
    params?: { page?: number; limit?: number; noteType?: string }
  ): Promise<PaginatedResponse<unknown>> {
    const queryParams = params ? buildQueryParams(params as Record<string, unknown>) : {};

    const response = await apiClient.get<PaginatedResponse<unknown>>(
      `${this.basePath}/${id}/notes`,
      { params: queryParams }
    );

    if (response.data.success && response.data.data) {
      return response.data;
    }

    throw new Error('Failed to fetch deal notes');
  }

  /**
   * Duplicate a deal
   * @param id - Deal ID to duplicate
   * @returns New duplicated deal
   */
  async duplicate(id: string): Promise<Deal> {
    const response = await apiClient.post<ApiResponse<Deal>>(`${this.basePath}/${id}/duplicate`);

    if (response.data.success && response.data.data) {
      return response.data.data;
    }

    throw new Error('Failed to duplicate deal');
  }

  /**
   * Archive a deal (mark as completed)
   * @param id - Deal ID
   * @returns Updated deal with completed stage
   * @deprecated Use updateStage with 'completed' instead
   */
  async archive(id: string): Promise<Deal> {
    return this.updateStage(id, {
      stage: 'completed',
      note: 'Deal archived',
    });
  }

  /**
   * Mark deal as lost
   * @param id - Deal ID
   * @param note - Optional note explaining why deal was lost
   * @returns Updated deal with completed stage
   * @deprecated Use updateStage with 'completed' and appropriate note instead
   */
  async markAsLost(id: string, note?: string): Promise<Deal> {
    return this.updateStage(id, {
      stage: 'completed',
      note: note || 'Deal marked as lost',
    });
  }

  /**
   * Mark deal as completed
   * @param id - Deal ID
   * @param note - Optional completion note
   * @returns Updated deal with completed stage
   */
  async markAsCompleted(id: string, note?: string): Promise<Deal> {
    return this.updateStage(id, {
      stage: 'completed',
      note: note || 'Deal completed successfully',
    });
  }
}

/**
 * Export singleton instance
 */
export const dealService = new DealService();

/**
 * Export default
 */
export default dealService;
